import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from '../modal/Modal';
import { Row, getItemWithExpiration } from '../../lib/utils';
import { BoldText, RegularText } from '../../lib/Text';
import { colors } from '../../lib/colors';
import { useMemberNotificationSettings } from '../../services/useMemberNotificationSettings';
import {
    MemberNotificationKind,
    MemberNotificationSettings,
    MemberNotificationSettingsDraft,
    MemberNotificationSettingsPatchBody,
    buildMemberNotificationSettingsPatch,
    classifyMemberNotificationSettingsResult,
    createMemberNotificationSettingsDraft,
    getKindSendState,
    getKindsTurningAllOff,
    getMemberNotificationSaveFailureMessage,
    getRenderableTimings,
    hasMemberNotificationSettingsChanges,
    setDraftKindAll,
    setDraftTiming,
} from '../../lib/memberNotificationSettings';
import {
    MEMBER_NOTIFICATION_BULK_ACTION_LABEL,
    MEMBER_NOTIFICATION_KIND_COPY,
    MEMBER_NOTIFICATION_MODAL_COPY,
    MEMBER_NOTIFICATION_SEND_STATE_LABEL,
    MEMBER_NOTIFICATION_STATUS_COPY,
    MEMBER_NOTIFICATION_TIMING_COPY,
} from '../../lib/memberNotificationSettingsCopy';

type Props = {
    setIsVisible: (visible: boolean) => void;
};

type Confirmation =
    | { type: 'discard' }
    | {
          type: 'allOff';
          kinds: MemberNotificationKind[];
          body: MemberNotificationSettingsPatchBody;
      };

const TITLE_ID = 'member-notification-settings-title';
const DESCRIPTION_ID = 'member-notification-settings-description';
const KIND_ORDER: MemberNotificationKind[] = ['membershipExpiryNotice', 'memberTicketExpiryNotice'];

const getSendStateStyle = (state: 'all' | 'partial' | 'none') => {
    if (state === 'all') return { backgroundColor: '#E5F8F3', color: '#007A6B' };
    if (state === 'partial') return { backgroundColor: '#FFF7E8', color: '#B45309' };
    return { backgroundColor: '#F2F4F6', color: '#6E6E6E' };
};

const Notice = ({ tone, children }: { tone: 'info' | 'warning' | 'error' | 'success'; children: React.ReactNode }) => {
    const styles = {
        info: { border: '#C3D4F4', background: '#ECF1FC', color: '#2B529D' },
        warning: { border: '#FDE68A', background: '#FFF7E8', color: '#92400E' },
        error: { border: '#FECACA', background: '#FEF2F2', color: '#B91C1C' },
        success: { border: '#B3E8DE', background: '#E5F8F3', color: '#007A6B' },
    }[tone];

    return (
        <div
            style={{
                border: `1px solid ${styles.border}`,
                background: styles.background,
                color: styles.color,
                borderRadius: 9,
                fontSize: 13,
                lineHeight: '19px',
                padding: '10px 12px',
                whiteSpace: 'pre-line',
            }}
        >
            {children}
        </div>
    );
};

const SettingsToggle = ({
    checked,
    disabled,
    labelledBy,
    describedBy,
    onChange,
}: {
    checked: boolean;
    disabled: boolean;
    labelledBy: string;
    describedBy?: string;
    onChange: (checked: boolean) => void;
}) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        style={{
            width: 44,
            height: 26,
            borderRadius: 13,
            backgroundColor: disabled ? (checked ? '#AFC6EE' : '#C7CDD5') : checked ? colors.primary : '#87919E',
            position: 'relative',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s ease',
            flexShrink: 0,
        }}
        className="focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
    >
        <span
            aria-hidden="true"
            style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: 'white',
                position: 'absolute',
                top: 3,
                left: checked ? 21 : 3,
                transition: 'left 0.2s ease',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.18)',
            }}
        />
    </button>
);

const ConfirmationDialog = ({
    title,
    paragraphs,
    confirmLabel,
    destructive = false,
    onCancel,
    onConfirm,
}: {
    title: string;
    paragraphs?: string[];
    confirmLabel: string;
    destructive?: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}) => {
    const cancelButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        cancelButtonRef.current?.focus();
    }, []);

    return (
        <Modal
            onClose={onCancel}
            whiteViewWidth={480}
            borderRadius={16}
            isWhiteOverFlowScroll={false}
            backgroundOpacity={0.42}
        >
            <div role="alertdialog" aria-modal="true" aria-label={title} style={{ padding: '26px 28px 24px' }}>
                <BoldText fontSize={18} lineHeight={26} color="#3D4244">
                    {title}
                </BoldText>
                {!!paragraphs?.length && (
                    <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {paragraphs.map((paragraph) => (
                            <RegularText key={paragraph} fontSize={14} lineHeight={21} color="#62656B">
                                {paragraph}
                            </RegularText>
                        ))}
                    </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 26 }}>
                    <button
                        ref={cancelButtonRef}
                        type="button"
                        onClick={onCancel}
                        className="h-[40px] min-w-[84px] rounded-[9px] bg-[#F2F4F6] px-[18px] text-[14px] text-[#3D4244] hover:bg-[#E8EAED] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    >
                        {MEMBER_NOTIFICATION_MODAL_COPY.discardConfirm.cancel}
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        className="h-[40px] min-w-[84px] rounded-[9px] px-[18px] text-[14px] text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                        style={{ backgroundColor: destructive ? colors.red : colors.primary }}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

const LoadState = ({
    phase,
    onRetry,
}: {
    phase: 'loading' | 'unavailable' | 'forbidden' | 'error';
    onRetry: () => void;
}) => {
    if (phase === 'loading') {
        return (
            <div role="status" aria-live="polite" className="flex min-h-[210px] flex-col items-center justify-center gap-[12px]">
                <span
                    aria-hidden="true"
                    className="h-[28px] w-[28px] animate-spin rounded-full border-[3px] border-[#DDE5F4] border-t-primary"
                />
                <RegularText fontSize={14} lineHeight={20} color={colors.Text_2}>
                    {MEMBER_NOTIFICATION_STATUS_COPY.load.loading}
                </RegularText>
            </div>
        );
    }

    const message = MEMBER_NOTIFICATION_STATUS_COPY.load[phase];
    const canRetry = phase === 'unavailable' || phase === 'error';
    return (
        <div role="alert" className="flex min-h-[210px] flex-col items-center justify-center gap-[16px] text-center">
            <RegularText fontSize={14} lineHeight={21} color="#3D4244">
                {message}
            </RegularText>
            {canRetry && (
                <button
                    type="button"
                    onClick={onRetry}
                    className="h-[38px] rounded-[9px] border border-primary px-[18px] text-[14px] text-primary hover:bg-[#ECF1FC] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                    {MEMBER_NOTIFICATION_MODAL_COPY.retry}
                </button>
            )}
        </div>
    );
};

const SettingsSection = ({
    kind,
    settings,
    draft,
    disabled,
    onTimingChange,
    onBulkChange,
}: {
    kind: MemberNotificationKind;
    settings: MemberNotificationSettings;
    draft: MemberNotificationSettingsDraft;
    disabled: boolean;
    onTimingChange: (kind: MemberNotificationKind, timing: string, value: boolean) => void;
    onBulkChange: (kind: MemberNotificationKind, value: boolean) => void;
}) => {
    const copy = MEMBER_NOTIFICATION_KIND_COPY[kind];
    const timings = getRenderableTimings(settings, kind);
    const sendState = getKindSendState(settings, draft, kind);
    const hasOffTiming = timings.some((timing) => draft[kind][timing] === false);
    const turnAllOn = sendState === 'none';

    return (
        <section aria-labelledby={`${kind}-title`} style={{ padding: '22px 0' }}>
            <div className="flex items-start justify-between gap-[16px]">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-[8px]">
                        <BoldText fontSize={16} lineHeight={24} color="#3D4244" className="break-keep">
                            <span id={`${kind}-title`}>{copy.title}</span>
                        </BoldText>
                        {sendState && (
                            <span
                                style={{
                                    ...getSendStateStyle(sendState),
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    height: 22,
                                    padding: '0 8px',
                                    borderRadius: 11,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    lineHeight: '22px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {MEMBER_NOTIFICATION_SEND_STATE_LABEL[sendState]}
                            </span>
                        )}
                    </div>
                    <div className="mt-[5px] text-[13px] leading-[19px] text-[#62656B]">{copy.schedule}</div>
                </div>
                {sendState && (
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => onBulkChange(kind, turnAllOn)}
                        className="shrink-0 rounded-[7px] px-[6px] py-[3px] text-[13px] font-medium text-primary hover:bg-[#ECF1FC] focus:outline-none focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        {turnAllOn
                            ? MEMBER_NOTIFICATION_BULK_ACTION_LABEL.turnAllOn
                            : MEMBER_NOTIFICATION_BULK_ACTION_LABEL.turnAllOff}
                    </button>
                )}
            </div>

            <div className="mt-[9px] text-[12px] leading-[18px] text-[#80858C]">{copy.scope}</div>

            <div className="mt-[14px] overflow-hidden rounded-[10px] border border-[#E5E8EB] bg-white">
                {timings.map((timing, index) => {
                    const timingCopy = MEMBER_NOTIFICATION_TIMING_COPY[kind][timing];
                    const checked = draft[kind][timing] === true;
                    const labelId = `${kind}-${timing}-label`;
                    const descriptionId = timingCopy.example ? `${kind}-${timing}-description` : undefined;
                    return (
                        <div
                            key={timing}
                            className="flex min-h-[60px] items-center justify-between gap-[16px] px-[14px] py-[11px]"
                            style={{ borderTop: index === 0 ? 'none' : '1px solid #F2F4F6' }}
                        >
                            <div className="min-w-0">
                                <div id={labelId} className="text-[14px] font-medium leading-[20px] text-[#3D4244]">
                                    {timingCopy.title}
                                </div>
                                {timingCopy.example && (
                                    <div
                                        id={descriptionId}
                                        className="mt-[2px] text-[12px] leading-[18px] text-[#7B858C]"
                                    >
                                        {timingCopy.example}
                                    </div>
                                )}
                            </div>
                            <SettingsToggle
                                checked={checked}
                                disabled={disabled}
                                labelledBy={`${kind}-title ${labelId}`}
                                describedBy={descriptionId}
                                onChange={(value) => onTimingChange(kind, timing, value)}
                            />
                        </div>
                    );
                })}
            </div>

            {copy.info && (
                <div className="mt-[12px] flex items-start gap-[6px] text-[12px] leading-[18px] text-[#6F777D]">
                    <span aria-hidden="true" className="shrink-0">
                        ⓘ
                    </span>
                    <span>{copy.info}</span>
                </div>
            )}
            {hasOffTiming && (
                <div className="mt-[10px]">
                    <Notice tone="warning">⚠ {copy.offWarning}</Notice>
                </div>
            )}
        </section>
    );
};

const ModalMemberNotificationSettings = ({ setIsVisible }: Props) => {
    const [authObject] = useState(() => getItemWithExpiration('authObject'));
    const settingsState = useMemberNotificationSettings({ centerId: authObject?.centerId });
    const { phase, settings, isReadOnly, isSaving, canEdit, refetch, save } = settingsState;
    const [draft, setDraft] = useState<MemberNotificationSettingsDraft | null>(null);
    const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
    const [saveFeedback, setSaveFeedback] = useState<{ tone: 'error' | 'success'; message: string } | null>(null);
    const saveFeedbackRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setDraft(settings ? createMemberNotificationSettingsDraft(settings) : null);
    }, [settings]);

    useEffect(() => {
        if (saveFeedback?.tone === 'error') saveFeedbackRef.current?.focus();
    }, [saveFeedback]);

    const latestCloseState = useRef({ settings, draft, confirmation, isSaving });
    latestCloseState.current = { settings, draft, confirmation, isSaving };

    const handleClose = useCallback(() => {
        const latest = latestCloseState.current;
        if (latest.confirmation) {
            setConfirmation(null);
            return;
        }
        if (latest.isSaving) return;
        if (latest.settings && latest.draft && hasMemberNotificationSettingsChanges(latest.settings, latest.draft)) {
            setConfirmation({ type: 'discard' });
            return;
        }
        setIsVisible(false);
    }, [setIsVisible]);

    const changeTiming = (kind: MemberNotificationKind, timing: string, value: boolean) => {
        if (!settings || !draft || !canEdit) return;
        setDraft(setDraftTiming(draft, settings, kind, timing, value));
        setSaveFeedback(null);
    };

    const changeKindAll = (kind: MemberNotificationKind, value: boolean) => {
        if (!settings || !draft || !canEdit) return;
        setDraft(setDraftKindAll(draft, settings, kind, value));
        setSaveFeedback(null);
    };

    const persist = async (body: MemberNotificationSettingsPatchBody) => {
        setSaveFeedback(null);
        const result = await save(body);
        const resultClass = classifyMemberNotificationSettingsResult(result);
        if (result.status === 200) {
            setSaveFeedback({ tone: 'success', message: MEMBER_NOTIFICATION_STATUS_COPY.saved });
            return;
        }
        const message = getMemberNotificationSaveFailureMessage(resultClass);
        setSaveFeedback(message ? { tone: 'error', message } : null);
    };

    const handleSave = () => {
        if (!settings || !draft || !canEdit) return;
        const body = buildMemberNotificationSettingsPatch(settings, draft);
        if (!body) return;
        const kindsTurningAllOff = getKindsTurningAllOff(settings, draft);
        if (kindsTurningAllOff.length > 0) {
            setConfirmation({ type: 'allOff', kinds: kindsTurningAllOff, body });
            return;
        }
        void persist(body);
    };

    const pendingPatch = settings && draft ? buildMemberNotificationSettingsPatch(settings, draft) : null;
    const hasChanges = !!pendingPatch;
    const changedCount = pendingPatch
        ? KIND_ORDER.reduce((count, kind) => count + Object.keys(pendingPatch[kind] || {}).length, 0)
        : 0;
    const saveDisabled = !canEdit || !hasChanges;

    const allOffConfirmation = confirmation?.type === 'allOff' ? confirmation : null;
    const allOffTitle =
        allOffConfirmation?.kinds.length === 2
            ? MEMBER_NOTIFICATION_MODAL_COPY.allOffConfirmTitle
            : allOffConfirmation
            ? MEMBER_NOTIFICATION_KIND_COPY[allOffConfirmation.kinds[0]].confirmAllOff.title
            : '';
    const allOffParagraphs = allOffConfirmation?.kinds.map(
        (kind) => MEMBER_NOTIFICATION_KIND_COPY[kind].confirmAllOff.body,
    );

    return (
        <Modal onClose={handleClose} whiteViewWidth={560} borderRadius={20} isWhiteOverFlowScroll={false}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={TITLE_ID}
                aria-describedby={DESCRIPTION_ID}
                style={{
                    maxHeight: '80vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    wordBreak: 'keep-all',
                    overflowWrap: 'anywhere',
                }}
            >
                <Row
                    style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '20px 24px 16px',
                        borderBottom: '1px solid #F2F4F6',
                        flexShrink: 0,
                    }}
                >
                    <div id={TITLE_ID}>
                        <BoldText fontSize={18} lineHeight={26} color="#3D4244">
                            {MEMBER_NOTIFICATION_MODAL_COPY.title}
                        </BoldText>
                    </div>
                    <button
                        type="button"
                        aria-label={MEMBER_NOTIFICATION_MODAL_COPY.close}
                        disabled={isSaving}
                        onClick={handleClose}
                        style={{
                            padding: '4px 8px',
                            borderRadius: 6,
                            fontSize: 18,
                            color: colors.LAVEL_4,
                            lineHeight: 1,
                        }}
                        className="hover:bg-[#F2F4F6] focus:outline-none focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        ✕
                    </button>
                </Row>

                <div style={{ padding: '18px 24px 0', overflowY: 'auto', flex: '1 1 auto' }}>
                    <div id={DESCRIPTION_ID} className="mb-[4px] flex flex-col">
                        {MEMBER_NOTIFICATION_MODAL_COPY.description.map((line) => (
                            <RegularText key={line} fontSize={13} lineHeight={20} color="#62656B">
                                {line}
                            </RegularText>
                        ))}
                    </div>

                    {phase !== 'ready' && <LoadState phase={phase} onRetry={refetch} />}

                    {phase === 'ready' && settings && draft && (
                        <div data-testid="member-notification-settings-ready">
                            {isReadOnly && (
                                <div role="alert" className="mt-[14px]">
                                    <Notice tone="info">{MEMBER_NOTIFICATION_STATUS_COPY.readOnly}</Notice>
                                </div>
                            )}
                            {KIND_ORDER.map((kind, index) => (
                                <div key={kind} style={{ borderTop: index === 0 ? 'none' : '1px solid #E8EAED' }}>
                                    <SettingsSection
                                        kind={kind}
                                        settings={settings}
                                        draft={draft}
                                        disabled={!canEdit}
                                        onTimingChange={changeTiming}
                                        onBulkChange={changeKindAll}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {phase === 'ready' && settings && draft && (
                    <div
                        data-testid="member-notification-settings-footer"
                        style={{
                            borderTop: '1px solid #F2F4F6',
                            backgroundColor: 'white',
                            padding: '14px 24px 18px',
                            flexShrink: 0,
                        }}
                    >
                        <div className="text-[12px] leading-[18px] text-[#80858C]">
                            {MEMBER_NOTIFICATION_MODAL_COPY.footer.map((line) => (
                                <div key={line}>{line}</div>
                            ))}
                        </div>
                        <div className="mt-[12px] flex items-end justify-between gap-[12px]">
                            <div className="min-w-0 flex-1">
                                {saveFeedback ? (
                                    <div
                                        ref={saveFeedbackRef}
                                        role={saveFeedback.tone === 'error' ? 'alert' : 'status'}
                                        aria-live={saveFeedback.tone === 'error' ? 'assertive' : 'polite'}
                                        aria-atomic="true"
                                        tabIndex={saveFeedback.tone === 'error' ? -1 : undefined}
                                    >
                                        <Notice tone={saveFeedback.tone}>{saveFeedback.message}</Notice>
                                    </div>
                                ) : hasChanges ? (
                                    <div className="text-[12px] font-medium leading-[18px] text-[#52698F]">
                                        {MEMBER_NOTIFICATION_MODAL_COPY.changedCount(changedCount)}
                                    </div>
                                ) : null}
                            </div>
                            <div className="flex shrink-0 gap-[8px]">
                                <button
                                    type="button"
                                    disabled={isSaving}
                                    onClick={handleClose}
                                    className="h-[40px] min-w-[80px] rounded-[9px] bg-[#F2F4F6] px-[18px] text-[14px] text-[#3D4244] hover:bg-[#E8EAED] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    {MEMBER_NOTIFICATION_MODAL_COPY.cancel}
                                </button>
                                <button
                                    type="button"
                                    disabled={saveDisabled}
                                    onClick={handleSave}
                                    className="h-[40px] min-w-[80px] rounded-[9px] bg-primary px-[18px] text-[14px] text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-[#D4DBE8]"
                                >
                                    {isSaving ? MEMBER_NOTIFICATION_MODAL_COPY.saving : MEMBER_NOTIFICATION_MODAL_COPY.save}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {confirmation?.type === 'discard' && (
                    <ConfirmationDialog
                        title={MEMBER_NOTIFICATION_MODAL_COPY.discardConfirm.title}
                        confirmLabel={MEMBER_NOTIFICATION_MODAL_COPY.discardConfirm.confirm}
                        onCancel={() => setConfirmation(null)}
                        onConfirm={() => setIsVisible(false)}
                    />
                )}
                {allOffConfirmation && (
                    <ConfirmationDialog
                        title={allOffTitle}
                        paragraphs={allOffParagraphs}
                        confirmLabel={MEMBER_NOTIFICATION_KIND_COPY[allOffConfirmation.kinds[0]].confirmAllOff.confirm}
                        destructive
                        onCancel={() => setConfirmation(null)}
                        onConfirm={() => {
                            const body = allOffConfirmation.body;
                            setConfirmation(null);
                            void persist(body);
                        }}
                    />
                )}
            </div>
        </Modal>
    );
};

export default ModalMemberNotificationSettings;
